import React, { useRef, useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiMapPin } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import RechargeModal from "../Recharge/RechargeModal";
import PayToViewOfferModal from "./Components/PayToViewOfferModal";
import { useAuth } from "../../Context/AuthContext";
import axios from "../../../Utils/axiosInstance";
import SupplierRQMSection from "./Components/SupplierRQMSection";

const MIN_CARDS = 4;
const PLATFORM_FEE = 69;

const fillWithDummies = (realOffers) => {
  const dummiesNeeded = Math.max(0, MIN_CARDS - realOffers.length);
  const dummies = Array.from({ length: dummiesNeeded }, (_, i) => ({
    _id: `__dummy_${i}`,
    __isDummy: true,
  }));
  return [...realOffers, ...dummies];
};

const ComingSoonCard = () => (
  <div
    className="
      relative flex-shrink-0 rounded-2xl overflow-hidden
      w-[19rem] sm:w-[20rem] md:w-[22rem] lg:w-[29rem] xl:w-[27rem]
      flex flex-col md:flex-row
      h-auto md:h-[13rem]
      snap-center
    "
    style={{
      background: "linear-gradient(135deg, #fffde7 0%, #f9fbc0 100%)",
      border: "1.5px dashed #c8cc6a",
      boxShadow: "0 2px 12px rgba(180,185,80,0.10)",
    }}
  >
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "repeating-linear-gradient(45deg, rgba(200,210,80,0.06) 0px, rgba(200,210,80,0.06) 2px, transparent 2px, transparent 10px)",
      }}
    />

    {/* icon panel — on top for mobile, on the right for md+ */}
    <div className="order-first md:order-2 w-full h-32 sm:h-36 md:w-[40%] md:h-full flex-shrink-0 p-3 md:p-4 flex items-center justify-center">
      <div
        className="h-full w-[80%] md:h-[75%] md:w-[80%] rounded-2xl flex flex-col items-center justify-center gap-1"
        style={{ border: "1.5px dashed #c8cc6a", background: "rgba(255,255,255,0.35)" }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c0c860" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
          <path d="M12 6v6l4 2" />
        </svg>
        <span className="text-[9px] font-bold tracking-[0.15em] uppercase" style={{ color: "#c0c060" }}>
          Soon
        </span>
      </div>
    </div>

    {/* text content */}
    <div className="order-2 md:order-1 flex flex-col justify-between p-4 flex-1 min-w-0">
      <div>
        <div
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 mb-1.5"
          style={{ background: "rgba(180,180,60,0.13)" }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full animate-pulse"
            style={{ background: "#a0a830" }}
          />
          <span
            className="text-[10px] font-bold tracking-widest uppercase"
            style={{ color: "#7a8020" }}
          >
            Coming Soon
          </span>
        </div>
        <p className="text-[11px] leading-snug mt-0.5" style={{ color: "#a0a030" }}>
          Stay tuned for updates
        </p>
        <span className="text-xs font-semibold tracking-wide" style={{ color: "#b0b040" }}>
          -- DAYS Left
        </span>
      </div>
      <div
        className="inline-flex items-center rounded-lg px-3 py-1.5 w-fit whitespace-nowrap mt-2 md:mt-0"
        style={{
          background: "rgba(255,255,255,0.7)",
          border: "1px solid rgba(180,185,80,0.25)",
        }}
      >
        <span className="text-base md:text-lg font-bold opacity-60" style={{ color: "#bfc060" }}>
          Rs.--/kg
        </span>
      </div>
    </div>
  </div>
);


const OfferCard = ({ item, onOfferClick }) => {
  const { offer, productVariety } = item;
  const navigate = useNavigate();
  const daysLeft = offer?.daysLeft ?? 0;
  const price = offer?.pricing?.amount ?? 0;
  const priceType = offer?.pricing?.priceType ?? "Per Kg";
  const varietyName = productVariety?.varietyName ?? "Product";
  const grade = productVariety?.grade ?? "";
  const uploadedImg = offer?.media?.images?.find(Boolean) || offer?.media?.images?.[0];
  const img = offer?.bannerImg || uploadedImg || productVariety?.img || "";
  const offerId = offer?._id;
  const isLoggedIn = Boolean(localStorage.getItem("qqc_userToken"));
  const offerNumber = offer?.offerUniqueNumber;
  const locationStr =
    offer?.location?.formatted ||
    [offer?.location?.city, offer?.location?.state].filter(Boolean).join(", ") ||
    "";

  // Avoid "GRADE GRADE A" when the grade value already includes the word "Grade"
  const gradeLabel = grade
    ? /^grade\b/i.test(grade.trim())
      ? grade.trim().toUpperCase()
      : `GRADE ${grade}`
    : "";

  const [timeLeft, setTimeLeft] = useState(null);
  const isUrgent = daysLeft <= 3;

  useEffect(() => {
    if (!isUrgent || !offer?.validityPeriod?.expiryDate) return;
    const calcTime = () => {
      const diff = new Date(offer.validityPeriod.expiryDate) - new Date();
      if (diff <= 0) return setTimeLeft({ d: "00", h: "00", m: "00", s: "00" });
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft({
        d: String(d).padStart(2, "0"),
        h: String(h).padStart(2, "0"),
        m: String(m).padStart(2, "0"),
        s: String(s).padStart(2, "0"),
      });
    };
    calcTime();
    const interval = setInterval(calcTime, 1000);
    return () => clearInterval(interval);
  }, [isUrgent, offer?.validityPeriod?.expiryDate]);

  const handleClick = () => {
    const isExpired =
      offer?.validityPeriod?.isExpired ||
      (offer?.validityPeriod?.expiryDate &&
        new Date(offer.validityPeriod.expiryDate) < new Date());
    if (isExpired) {
      toast("🕐 This offer has expired. Stay tuned — it'll be back soon!", {
        icon: "⏳",
        style: { borderRadius: "10px", background: "#3b2012", color: "#fff" },
      });
      return;
    }
    if (!isLoggedIn) { navigate("/login"); return; }
    if (offerId) onOfferClick(item);
  };

  return (
    <div
      onClick={handleClick}
      className="
        relative flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer
        w-[90%] sm:w-[85%] lg:w-[29rem] xl:w-[28rem] 2xl:w-[26rem]
        flex flex-col md:flex-row
        h-auto md:h-[13rem]
        snap-center
      "
      style={{ background: "var(--card-bg, #f0f5d8)" }}
    >
      {/* image — on top for mobile (column), on the right for md+ (row) */}
      <div className="order-first md:order-2 w-full h-36 sm:h-40 md:w-[40%] md:h-full flex-shrink-0 p-3 md:p-4 flex items-center justify-center overflow-hidden">
        {img ? (
          <img src={img} alt={varietyName} className="h-full w-full object-cover drop-shadow-md rounded-2xl" />
        ) : (
          <div className="h-full w-full bg-gray-100 rounded-lg" />
        )}
      </div>

      {/* text content */}
      <div className="order-2 md:order-1 flex flex-col justify-between p-3 md:p-3 flex-1 min-w-0 md:overflow-hidden">
        <div className="min-w-0">
          <h3 className="text-sm sm:text-base md:text-lg font-extrabold text-[#3b2012] leading-tight line-clamp-2 break-words">
            {varietyName}
          </h3>
          {offerNumber && (
            <span className="inline-block mt-0.5 text-[12px] md:text-xs font-bold text-primaryColor tracking-wide">
              {offerNumber} <span className="text-gray-600 font-semibold">({gradeLabel || "N/A"})</span>
            </span>
          )}
          <div className="mt-1 flex flex-col gap-0.5 min-w-0">
            <p className="text-[10px] tracking-wide leading-snug break-words">
              <span className="text-gray-400 font-semibold">Offer Quantity: </span>
              <span className="font-extrabold 0text-[11px] break-words" style={{ color: "#3b2012" }}>
                {(() => {
                  const qty = offer?.quantity;
                  if (!qty?.weightInNumber) return "N/A";
                  const unit = qty.weightIn ? ` ${qty.weightIn}` : "";
                  return `${qty.weightInNumber.toLocaleString()}${unit}`;
                })()}
              </span>
            </p>

            {locationStr ? (
              <div className="flex items-center gap-1 mt-0.5 text-[10.5px] md:text-xs font-semibold text-[#3b2012]/85 truncate">
                <FiMapPin className="text-primaryColor w-3 h-3 flex-shrink-0" />
                <span className="truncate">{locationStr}</span>
              </div>
            ) : null}
          </div>

          {(() => {
            const isExpired =
              offer?.validityPeriod?.isExpired ||
              (offer?.validityPeriod?.expiryDate &&
                new Date(offer.validityPeriod.expiryDate) < new Date());
            if (isExpired)
              return <span className="text-xs font-semibold text-gray-400 tracking-wide">Coming Soon Again ☺️!</span>;
            return isUrgent && timeLeft ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse mr-0.5 flex-shrink-0" />
                {[
                  { val: timeLeft.d, label: "Day" },
                  { val: timeLeft.h, label: "Hr" },
                  { val: timeLeft.m, label: "Min" },
                  { val: timeLeft.s, label: "Sec" },
                ].map(({ val, label }, i, arr) => (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center justify-center rounded-md px-1.5 py-0.5 min-w-[28px]"
                      style={{ background: "rgba(192,57,43,0.10)", border: "1px solid rgba(192,57,43,0.20)" }}>
                      <span className="text-[12px] font-extrabold leading-none tabular-nums" style={{ color: "#c0392b" }}>{val}</span>
                      <span className="text-[7px] font-bold uppercase tracking-wider leading-none mt-[2px]" style={{ color: "rgba(192,57,43,0.65)" }}>{label}</span>
                    </div>
                    {i < arr.length - 1 && (
                      <span className="text-[11px] font-black mb-2 leading-none" style={{ color: "rgba(192,57,43,0.50)" }}>:</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <span className="text-xs font-semibold text-gray-600 tracking-wide">
                {String(daysLeft).padStart(2, "0")} DAYS Left
              </span>
            );
          })()}
        </div>
        {(() => {
          let totalAmount = offer?.dealValueWithGst ?? offer?.dealValue ?? null;

          if (totalAmount == null) {
            const qtyInKg = offer?.quantity?.weightInNumber
              ? offer.quantity.weightIn === "ton"
                ? offer.quantity.weightInNumber * 1000
                : offer.quantity.weightIn === "quintal"
                  ? offer.quantity.weightInNumber * 100
                  : offer.quantity.weightInNumber
              : null;

            totalAmount = qtyInKg && price ? qtyInKg * price : null;
          }

          const roundedPrice = Math.round(price);
          const roundedTotal =
            totalAmount !== null ? Math.round(totalAmount) : null;

          return (
            <div className="flex flex-row items-center gap-1.5 sm:gap-2 mt-2 flex-wrap min-w-0 max-w-full">
              {/* Unit Price Pill */}
              <div className="inline-flex flex-col items-start bg-white rounded-lg px-2 py-1 shadow-xs border border-gray-100/60 min-w-0 max-w-full">
                <span className="text-xs sm:text-sm font-extrabold text-[#3b2012] leading-tight whitespace-nowrap">
                  Rs.{roundedPrice}
                </span>
                <span className="text-[7.5px] sm:text-[8px] font-bold text-[#3b2012]/70 uppercase tracking-wider leading-tight whitespace-nowrap mt-0.5">
                  PRICE PER {priceType.replace(/per/gi, "").replace(/price/gi, "").trim().toUpperCase() || "KG"}
                </span>
              </div>

              {/* Deal Value Pill */}
              {roundedTotal !== null && (
                <div
                  className="inline-flex flex-col items-start rounded-lg px-2 py-1 min-w-0 max-w-full"
                  style={{
                    background: "rgba(59, 32, 18, 0.08)",
                    border: "1px solid rgba(59, 32, 18, 0.15)",
                  }}
                >
                  <span className="text-xs sm:text-sm font-extrabold text-[#3b2012] leading-tight whitespace-nowrap">
                    Rs.{roundedTotal.toLocaleString("en-IN")}
                  </span>
                  <span className="text-[7px] sm:text-[7.5px] font-extrabold text-[#3b2012]/75 uppercase tracking-wider leading-tight whitespace-nowrap mt-0.5">
                    DEAL VALUE (INC. GST)
                  </span>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const OfferRow = ({ items, cardBg, onOfferClick, viewAllButton }) => {
  const scrollRef = useRef();
  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -350 : 350, behavior: "smooth" });
  };
  if (!items?.length) return null;
  return (
    <>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-mandatory snap-x pb-2 hide-scrollbar"
        style={{ "--card-bg": cardBg }}
      >
        {items.map((item, i) =>
          item.__isDummy ? (
            <ComingSoonCard key={item._id} />
          ) : (
            <OfferCard key={item?._id ?? i} item={item} onOfferClick={onOfferClick} />
          )
        )}
      </div>
      <div className="flex items-center justify-center gap-2 mt-5">
        <button onClick={() => scroll("left")} className="w-8 h-8 lg:w-9 lg:h-9 flex items-center bg-primaryBtnColor justify-center rounded-full border border-primaryBtnHoverColor hover:bg-primaryBtnHoverColor text-white transition">
          <FiChevronLeft />
        </button>
        {viewAllButton}
        <button onClick={() => scroll("right")} className="w-8 h-8 lg:w-9 lg:h-9 flex items-center bg-primaryBtnColor justify-center rounded-full border border-primaryBtnHoverColor hover:bg-primaryBtnHoverColor text-white transition">
          <FiChevronRight />
        </button>
      </div>
    </>
  );
};

// ── Main Sale component ───────────────────────────────────────────────────────
const Sale = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("qqc_userToken");
  const isLoggedIn = Boolean(token);
  const { user: storedUser } = useAuth(); // Assuming you have a useAuth hook that provides user data
  const buyerId = storedUser?._id ?? storedUser?.id ?? null;
  const [hotDeals, setHotDeals] = useState([]);
  const [recommendedDeals, setRecommendedDeals] = useState([]);
  const [totals, setTotals] = useState({ hot: 0, recommended: 0 });
  const [loading, setLoading] = useState(false);

  // ── Pay-to-view gate state ─────────────────────────────────────────────
  const [wallet, setWallet] = useState(null);
  const [payModal, setPayModal] = useState({ show: false, offer: null });
  const [checkingOfferId, setCheckingOfferId] = useState(null);

  useEffect(() => {
    const fetchHotDeals = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/offer/get-hot-deals`
        );
        if (data.success) {
          setHotDeals(data.data.Hot_Deals ?? []);
          setTotals((prev) => ({ ...prev, hot: data.data.total ?? 0 }));
        }
      } catch (err) {
        console.error("Failed to fetch hot deals:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHotDeals();
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !buyerId) return;

    const fetchRecommendedDeals = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/offer/get-offer-for-buyer/${buyerId}`,
        );
        if (data.success) {
          setRecommendedDeals(data.data.Recommended_Deal_For_You ?? []);
          setTotals((prev) => ({
            ...prev,
            recommended: data.data.total?.recommended ?? 0,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch recommended deals:", err);
      }
    };

    fetchRecommendedDeals();
  }, [isLoggedIn, buyerId]);

  // ── Fetch wallet (logged-in only) — needed for the pay-to-view modal ────
  useEffect(() => {
    if (!isLoggedIn || !buyerId) return;
    const fetchWallet = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/wallet/get-wallet-balance/${buyerId}`,
        );
        setWallet(data.data);
      } catch (err) {
        console.error("Failed to fetch wallet:", err);
      }
    };
    fetchWallet();
  }, [isLoggedIn, buyerId]);

  // ── Click handler: navigate directly to post-order page where payment gate exists ─
  const handleOfferClick = (item) => {
    const offerId = item?.offer?._id;
    if (!offerId) return;
    navigate(`/post-order-via-offer?offerId=${offerId}`);
  };

  const handlePaid = (offerId) => {
    setPayModal({ show: false, offer: null });
    navigate(`/post-order-via-offer?offerId=${offerId}`);
  };

  const SectionSkeleton = () => (
    <div className="flex gap-4 overflow-hidden">
      {[1, 2, 3].map((n) => (
        <div key={n} className="flex-shrink-0 w-[18rem] md:w-[21rem] lg:w-[28rem] h-[10rem] md:h-[11rem] rounded-2xl bg-gray-100 animate-pulse" />
      ))}
    </div>
  );

  const ViewAllButton = ({ type, total }) => {
    return (
      <button
        onClick={() => navigate(`/all-offers?type=${type}`)}
        className="
          flex items-center gap-2 px-4 py-2 rounded-full
          border-2 border-primaryColor text-primaryColor
          text-sm font-semibold
          hover:bg-primaryColor hover:text-white
          transition-all duration-200 active:scale-95
        "
      >
        View All
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primaryColor/10 text-xs font-bold">
          {total}
        </span>
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
        </svg>
      </button>
    );
  };

  if (storedUser?.role === "supplier") {
    return <SupplierRQMSection />;
  }

  return (
    <div className="w-full pb-12 md:pb-16 px-4 md:px-8">
      <div className="2xl:max-w-7xl mx-auto py-3 space-y-10">

        <section className="mt-2">
          <h2 className="text-3xl text-center md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
            Hot Deals
          </h2>
          <p className="my-4 text-xs text-center md:w-[60%] mx-auto md:text-sm text-gray-500 mt-2 leading-relaxed">
            The Best Platform For B2B Business Growth And Global Trade Opportunities. Create Your Requirements Here And Wait For The Best Quotation
          </p>
          {loading ? <SectionSkeleton /> : (
            <>
              <OfferRow
                items={fillWithDummies(hotDeals)}
                cardBg="#defcb7"
                onOfferClick={handleOfferClick}
                viewAllButton={<ViewAllButton type="hot" total={totals.hot} />}
              />
            </>
          )}
        </section>

        {isLoggedIn && (
          <section className="mt-2">
            <h2 className="text-3xl text-center md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
              Recommended Deals for You
            </h2>
            <p className="my-4 text-xs text-center md:w-[60%] mx-auto md:text-sm text-gray-500 mt-2 leading-relaxed">
              The Best Platform For B2B Business Growth And Global Trade Opportunities. Create Your Requirements Here And Wait For The Best Quotation
            </p>
            {loading ? <SectionSkeleton /> : (
              <>
                <OfferRow
                  items={fillWithDummies(recommendedDeals)}
                  cardBg="#FBFBB7"
                  onOfferClick={handleOfferClick}
                  viewAllButton={<ViewAllButton type="recommended" total={totals.recommended} />}
                />
              </>
            )}
          </section>
        )}
      </div>

      <PayToViewOfferModal
        show={payModal.show}
        offer={payModal.offer}
        wallet={wallet}
        user={storedUser}
        onClose={() => setPayModal({ show: false, offer: null })}
        onPaid={handlePaid}
      />

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default Sale;